import React, { useState, useEffect } from 'react';
import { fetchSettings, updateSettings } from '../api/AccountSettingsService';
import { Alert } from 'react-bootstrap';
import './AccountSettingsComponent.css';

const AccountSettingsComponent = () => {
  // Single state object for all form values
  const [formValues, setFormValues] = useState({
    profileSetting: '',
    contacts: '',
    favorited: '',
    eventsEmails: '',
    weekly: '',
    upgradeBlock: '',
    upgradeVisibility: '',
    upgradeImages: '',
    upgradeEvents: '',
    upgradeMatches: ''
  });
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const token = localStorage.getItem('token');
  const memberTypeId = localStorage.getItem('currentUserMemberTypeId');
  const isPremiumMember = memberTypeId !== '1';

  // Options configuration for all select fields
  const selectOptions = {
    profileSetting: [
      { id: "1", code: "PROFILE_SHOW", detail: "Show" },
      { id: "2", code: "PROFILE_HIDE", detail: "Hide" }
    ],
    contacts: [
      { id: "1", code: "CONTACTS_EMAIL", detail: "Send Email" },
      { id: "2", code: "CONTACTS_NO_EMAIL", detail: "No Email" }
    ],
    favorited: [
      { id: "1", code: "FAVORITED_EMAIL", detail: "Send Email" },
      { id: "2", code: "FAVORITED_NO_EMAIL", detail: "No Email" }
    ],
    eventsEmails: [
      { id: "1", code: "EVENTS_EMAIL", detail: "Send Email" },
      { id: "2", code: "EVENTS_NO_EMAIL", detail: "No Email" }
    ],
    weekly: [
      { id: "1", code: "WEEKLY_EMAIL", detail: "Send Email" },
      { id: "2", code: "WEEKLY_NO_EMAIL", detail: "No Email" }
    ],
    upgradeBlock: [
      { id: "1", code: "UPGRADED_BLOCK_FREE", detail: "No Email" },
      { id: "2", code: "UPGRADED_BLOCK_ALLOW", detail: "Send Email" }
    ],
    upgradeVisibility: [
      { id: "1", code: "UPGRADED_VISIBILITY_BLOCK", detail: "Block Profile" },
      { id: "2", code: "UPGRADED_VISIBILITY_SHOW", detail: "Show Profile" }
    ],
    upgradeImages: [
      { id: "1", code: "UPGRADED_IMAGES_EMAIL", detail: "Send Email" },
      { id: "2", code: "UPGRADED_IMAGES_NO_EMAIL", detail: "No Email" }
    ],
    upgradeEvents: [
      { id: "1", code: "UPGRADED_EMAIL", detail: "Send Email" },
      { id: "2", code: "UPGRADED_NO_EMAIL", detail: "No Email" }
    ],
    upgradeMatches: [
      { id: "1", code: "UPGRADED_MATCHES_EMAIL", detail: "Send Email" },
      { id: "2", code: "UPGRADED_MATCHES_NO", detail: "No Email" }
    ]
  };

  // Load settings data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError("Authentication token not found. Please log in again.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetchSettings(token);
        const data = response.data[0];
        console.log("Received settings data:", data);
        
        // Extract settings from array
        // The data structure is an array with indexes corresponding to specific settings
        const settingsMap = {
          profileSetting: data[10] || '',
          contacts: data[11] || '',
          favorited: data[12] || '',
          eventsEmails: data[13] || '',
          weekly: data[14] || '',
          upgradeBlock: data[15] || '',
          upgradeVisibility: data[16] || '',
          upgradeImages: data[17] || '',
          upgradeEvents: data[18] || '',
          upgradeMatches: data[19] || ''
        };
        
        console.log("Mapped settings:", settingsMap);
        
        // Process settings and set form values
        const newFormValues = {};
        
        Object.entries(settingsMap).forEach(([stateKey, apiCode]) => {
          if (apiCode) {
            // Find the matching option for this code
            const option = selectOptions[stateKey].find(opt => opt.code === apiCode);
            if (option) {
              newFormValues[stateKey] = option.detail;
              console.log(`Setting ${stateKey} to ${option.detail} (from code ${apiCode})`);
            } else {
              console.log(`No option found for ${stateKey} with code ${apiCode}`);
              newFormValues[stateKey] = '';
            }
          } else {
            console.log(`No value for ${stateKey}`);
            newFormValues[stateKey] = '';
          }
        });
        
        setFormValues(newFormValues);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching settings:', error);
        setError("Failed to load settings. Please try again.");
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [token]);
  
  // Handle form input changes
  const handleInputChange = (fieldId, value) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setError('');
    setIsSubmitting(true);
    
    try {
      // Construct the payload for API
      const updatedSettingsColumns = {};
      
      // Map all options to Y/N values
      Object.entries(selectOptions).forEach(([fieldId, options]) => {
        options.forEach(option => {
          const key = `P_${option.code}`;
          updatedSettingsColumns[key] = formValues[fieldId] === option.detail ? 'Y' : 'N';
        });
      });
      
      console.log("Sending updated settings:", updatedSettingsColumns);
      await updateSettings(token, updatedSettingsColumns);
      
      // Show success message
      setSuccessMessage('Settings updated successfully!');
      
      // Scroll to the top of the container to make sure the user sees the message
      const container = document.querySelector('.settings-container');
      if (container) {
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 15000);
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Failed to update settings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reusable select field component
  const SelectField = ({ id, label, options, value, onChange, disabled = false }) => (
    <div className="setting-field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="form-control"
        required
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(id, e.target.value)}
      >
        <option style={{ display: 'none' }} value="">
          [Not selected]
        </option>
        {options.map((option) => (
          <option key={option.id} value={option.detail}>
            {option.detail}
          </option>
        ))}
      </select>
    </div>
  );

  if (isLoading) {
    return <div className="settings-loading">Loading your settings...</div>;
  }

  return (
    <div className="settings-container">
      {successMessage && (
        <Alert 
          variant="success" 
          className="settings-alert success-alert"
          dismissible
          onClose={() => setSuccessMessage('')}
        >
          <Alert.Heading>Success!</Alert.Heading>
          <p>{successMessage}</p>
        </Alert>
      )}
      
      {error && (
        <Alert 
          variant="danger" 
          className="settings-alert error-alert"
          dismissible
          onClose={() => setError('')}
        >
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="settings-panel">
          <div className="settings-header">Basic member settings</div>
          
          <div className="settings-content">
            <div className="settings-grid">
              <SelectField
                id="profileSetting"
                label="Profile visibility:"
                options={selectOptions.profileSetting}
                value={formValues.profileSetting}
                onChange={handleInputChange}
              />
              
              <SelectField
                id="contacts"
                label="Email when user contacts:"
                options={selectOptions.contacts}
                value={formValues.contacts}
                onChange={handleInputChange}
              />
              
              <SelectField
                id="favorited"
                label="Email when favorited:"
                options={selectOptions.favorited}
                value={formValues.favorited}
                onChange={handleInputChange}
              />
              
              <SelectField
                id="eventsEmails"
                label="Emails from single events:"
                options={selectOptions.eventsEmails}
                value={formValues.eventsEmails}
                onChange={handleInputChange}
              />
              
              <SelectField
                id="weekly"
                label="Send weekly matches:"
                options={selectOptions.weekly}
                value={formValues.weekly}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="settings-header">Upgraded member settings</div>
          
          <div className="settings-content">
            <div className="settings-grid">
              <SelectField
                id="upgradeBlock"
                label="Block email from free members:"
                options={selectOptions.upgradeBlock}
                value={formValues.upgradeBlock}
                onChange={handleInputChange}
                disabled={!isPremiumMember}
              />
              
              <SelectField
                id="upgradeVisibility"
                label="Block visibility from free members:"
                options={selectOptions.upgradeVisibility}
                value={formValues.upgradeVisibility}
                onChange={handleInputChange}
                disabled={!isPremiumMember}
              />
              
              <SelectField
                id="upgradeImages"
                label="Block email without profile images:"
                options={selectOptions.upgradeImages}
                value={formValues.upgradeImages}
                onChange={handleInputChange}
                disabled={!isPremiumMember}
              />
              
              <SelectField
                id="upgradeEvents"
                label="Emails from upgraded member events:"
                options={selectOptions.upgradeEvents}
                value={formValues.upgradeEvents}
                onChange={handleInputChange}
                disabled={!isPremiumMember}
              />
              
              <SelectField
                id="upgradeMatches"
                label="Send upgraded weekly matches:"
                options={selectOptions.upgradeMatches}
                value={formValues.upgradeMatches}
                onChange={handleInputChange}
                disabled={!isPremiumMember}
              />
            </div>
          </div>
          
          <div className="settings-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Settings'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AccountSettingsComponent;